// ============================================================
// Hazard System — track-based environmental obstacles
// ============================================================

import { HazardDef, HazardType, TrackPoint, KART_RADIUS } from './types';

// ============================================================
// Runtime Hazard State
// ============================================================

export interface HazardState {
  def: HazardDef;
  x: number;
  z: number;
  active: boolean;         // for timed hazards: currently dangerous?
  timer: number;           // internal timer for timed/moving behavior
  baseX: number;           // original position for moving hazards
  baseZ: number;
  moveDir: number;         // movement direction for moving hazards
  radius: number;          // collision radius
}

// ============================================================
// Initialize hazards from map definitions
// ============================================================

export function initHazards(
  hazardDefs: HazardDef[],
  path: TrackPoint[],
  roadWidth: number
): HazardState[] {
  const n = path.length;
  const halfWidth = roadWidth / 2;

  return hazardDefs.map((def) => {
    const idx = Math.floor(def.pathFraction * n) % n;
    const p = path[idx];
    const lateralPos = def.lateralOffset * halfWidth * 0.8;
    const x = p.x + p.nx * lateralPos;
    const z = p.z + p.nz * lateralPos;

    return {
      def,
      x,
      z,
      active: def.behavior !== 'timed', // timed start off
      timer: 0,
      baseX: x,
      baseZ: z,
      moveDir: 1,
      radius: getHazardRadius(def.type),
    };
  });
}

function getHazardRadius(type: HazardType): number {
  switch (type) {
    case 'boulder': return 2.0;
    case 'laser_gate': return 1.5;
    case 'ghost': return 1.8;
    case 'ice_crack': return 3.0;
    default: return 1.5;
  }
}

// ============================================================
// Update hazard positions/states each tick
// ============================================================

export function updateHazards(hazards: HazardState[], dt: number, path: TrackPoint[], roadWidth: number): void {
  const halfWidth = roadWidth / 2;

  for (const h of hazards) {
    h.timer += dt;

    switch (h.def.behavior) {
      case 'moving': {
        // Move across the road back and forth
        const speed = h.def.speed ?? 5;
        const moveRange = halfWidth * 0.7;
        const offset = Math.sin(h.timer * speed * 0.5) * moveRange;

        const idx = Math.floor(h.def.pathFraction * path.length) % path.length;
        const p = path[idx];
        h.x = h.baseX + p.nx * offset;
        h.z = h.baseZ + p.nz * offset;
        break;
      }

      case 'timed': {
        const onTime = h.def.onTime ?? 3;
        const offTime = h.def.offTime ?? 2;
        const cycle = onTime + offTime;
        const phase = h.timer % cycle;
        h.active = phase < onTime;
        break;
      }

      case 'static':
      default:
        // Static hazards don't move
        break;
    }
  }
}

// ============================================================
// Check kart collision with hazards
// ============================================================

export function checkHazardCollision(
  hazards: HazardState[],
  kartX: number,
  kartZ: number,
  kartRadius: number = KART_RADIUS
): HazardState | null {
  for (const h of hazards) {
    if (!h.active) continue;

    const dx = kartX - h.x;
    const dz = kartZ - h.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist < h.radius + kartRadius) {
      return h;
    }
  }
  return null;
}
